import { useLingui } from '@lingui/react/macro';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Avatar, Box } from '@mui/material';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import { type KeyboardEvent, type MouseEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Button, useDialogStore } from 'ui-kit';
import { CryptoIcon } from '@/icons/crypto_icons';
import { useReferralCardData } from '../../features/referral/hooks/useReferralCardData.ts';
import useAppStore from '../../store/rootStore';
import { DelegationChips } from '../header/delegation_chips.tsx';
import {
  getHeaderWalletTriggerSx,
  headerDelegatedChipSx,
  headerWalletAddressSx,
  headerWalletCaretSx,
  headerWalletDotSx,
} from '../header/header_action_styles.ts';
import { isHoverMovingWithin, useHoverMenu } from '../header/use_hover_menu.ts';
import { type AccountMenuView, UserAccountMenu } from '../header/user_account_menu/index.tsx';

interface CustomConnectButtonProps {
  /** When set, the connected state renders the hover wallet menu instead of opening the account dialog. */
  accountMenu?: { canWithdraw: boolean };
  size?: 'small' | 'medium' | 'large';
}

const legacyChipSx = { mr: 1, height: 20, fontSize: '0.625rem' } as const;

export const CustomConnectButton = ({ accountMenu, size = 'large' }: CustomConnectButtonProps) => {
  const { t } = useLingui();
  const isGeoBlocked = useAppStore((state) => state.status === 'AUTHORITY_BLOCKED');
  const userCountry = useAppStore((state) => state.config?.userCountry);
  const { openDialog } = useDialogStore();
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRootRef = useRef<HTMLElement | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [menuView, setMenuView] = useState<AccountMenuView>('overview');
  const { referralLink, shouldDisplayData } = useReferralCardData();
  const showReferral = Boolean(shouldDisplayData && referralLink);
  const hasMenu = accountMenu != null;

  // Deposit / withdraw / fees views hold form and tx state, so hover-out must not dismiss them.
  const { open, mounted, handleEnter, handleLeave, handleToggle, handleClose, handleEscape } = useHoverMenu({
    pinned: menuView !== 'overview',
  });

  const setTriggerRef = useCallback((node: HTMLButtonElement | null) => {
    triggerRef.current = node;
    setAnchorEl(node);
  }, []);

  const setMenuRootEl = useCallback((node: HTMLElement | null) => {
    menuRootRef.current = node;
  }, []);

  useEffect(() => {
    if (!open) setMenuView('overview');
  }, [open]);

  const handleAccountButtonClick = useCallback(() => {
    if (hasMenu) handleToggle();
    else openDialog('accountDialog');
  }, [hasMenu, handleToggle, openDialog]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') handleEscape(triggerRef.current);
    },
    [handleEscape]
  );

  const handleTriggerLeave = useCallback(
    (event: MouseEvent) => {
      if (isHoverMovingWithin(event.relatedTarget, [menuRootRef.current])) return;
      handleLeave();
    },
    [handleLeave]
  );

  return (
    <ConnectButton.Custom>
      {({ account, chain, openChainModal, openConnectModal, authenticationStatus, mounted: rkMounted }) => {
        const ready = rkMounted && authenticationStatus !== 'loading';
        const connected = ready && account && chain && (!authenticationStatus || authenticationStatus === 'authenticated');

        return (
          <div
            {...(!ready && {
              'aria-hidden': true,
              style: {
                opacity: 0,
                pointerEvents: 'none',
                userSelect: 'none',
              },
            })}
          >
            {(() => {
              if (isGeoBlocked) {
                return (
                  <Button
                    variant="contained"
                    color="error"
                    size={size}
                    onClick={() => openDialog('geoBlockDialog', { countryCode: userCountry ?? 'us' })}
                  >
                    {t({ comment: 'Geo-blocked connect button', message: 'Unavailable in your location' })}
                  </Button>
                );
              }

              if (!connected) {
                return (
                  <Button onClick={openConnectModal} variant="contained" size={size}>
                    {t({ comment: 'Connect wallet button', message: 'Connect' })}
                  </Button>
                );
              }

              if (chain.unsupported) {
                return (
                  <Button onClick={openChainModal} variant="contained" color="error" size={size}>
                    {t({ comment: 'Wallet on unsupported chain button', message: 'Wrong network' })}
                  </Button>
                );
              }

              if (accountMenu) {
                return (
                  <>
                    <Box
                      ref={setTriggerRef}
                      component="button"
                      type="button"
                      onClick={handleAccountButtonClick}
                      onMouseEnter={handleEnter}
                      onMouseLeave={handleTriggerLeave}
                      onKeyDown={handleKeyDown}
                      aria-expanded={open}
                      aria-haspopup="true"
                      sx={getHeaderWalletTriggerSx(open)}
                    >
                      <Box sx={headerWalletDotSx} />
                      <DelegationChips sx={headerDelegatedChipSx} />
                      <Box component="span" sx={headerWalletAddressSx}>
                        {account.displayName}
                      </Box>
                      <KeyboardArrowDownIcon
                        sx={{
                          ...headerWalletCaretSx,
                          transition: 'transform 150ms cubic-bezier(0.16, 1, 0.3, 1)',
                          transform: open ? 'rotate(180deg)' : 'none',
                        }}
                      />
                    </Box>

                    <UserAccountMenu
                      anchorEl={anchorEl}
                      open={open}
                      mounted={mounted}
                      onClose={handleClose}
                      onEnter={handleEnter}
                      onLeave={handleLeave}
                      onRootRef={setMenuRootEl}
                      triggerEl={anchorEl}
                      canWithdraw={accountMenu.canWithdraw}
                      showReferral={showReferral}
                      ensName={account.ensName}
                      ensAvatar={account.ensAvatar}
                      view={menuView}
                      onViewChange={setMenuView}
                    />
                  </>
                );
              }

              return (
                <Box sx={{ display: 'inline-flex' }}>
                  <Button
                    onClick={handleAccountButtonClick}
                    variant="outlined"
                    size={size}
                    startIcon={
                      account.ensAvatar ? (
                        <Avatar src={account.ensAvatar} alt={account.displayName} sx={{ width: 20, height: 20 }} />
                      ) : (
                        <CryptoIcon name="MON" />
                      )
                    }
                    endIcon={<KeyboardArrowDownIcon />}
                  >
                    <DelegationChips sx={legacyChipSx} />
                    <Box
                      component="span"
                      sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 160 }}
                    >
                      {account.displayName}
                    </Box>
                  </Button>
                </Box>
              );
            })()}
          </div>
        );
      }}
    </ConnectButton.Custom>
  );
};
